/**
 * useFormDraft — автосохранение черновика формы модала + защита от случайного закрытия.
 *
 * Зачем: юзеры случайно кликают мимо модала (Новая Работа/Клиент/Объект/Расход)
 * и теряют введённое. Хук:
 *   1) хранит введённое в localStorage (только режим создания),
 *   2) восстанавливает при следующем открытии,
 *   3) чистит черновик ТОЛЬКО при явной «Отмена» или успешном сохранении.
 *
 * Закрытие по клику вне модала / Escape перехватывается useUnsavedGuard:
 * если форма «грязная» — показываем confirm, черновик при этом сохраняется.
 *
 * Все строки UI — снаружи через i18n (хук строк не содержит).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { track } from '../analytics';

const PREFIX = 'fixty:draft:';

// Пустой/осмысленный черновик? Сравниваем с базовой (pristine) формой.
function serialize(obj) {
  try { return JSON.stringify(obj); } catch { return ''; }
}

/**
 * @param {object}   opts
 * @param {string}   opts.scope     — уникальный ключ контекста (тип модала + привязка)
 * @param {object}   opts.form      — текущее состояние формы
 * @param {function} opts.setForm   — сеттер формы
 * @param {boolean}  opts.enabled   — включить персист (обычно: режим создания)
 * @returns {{ restored, isDirty, clearDraft, resetForm, dismissRestored }}
 */
export function useFormDraft({ scope, form, setForm, enabled = true }) {
  const storageKey = PREFIX + scope;
  // Базовая (пустая) форма — снимок первого рендера, ДО восстановления черновика.
  const baselineRef = useRef(serialize(form));
  const baselineObjRef = useRef(form);
  const [restored, setRestored] = useState(false);
  const saveTimerRef = useRef(null);

  const isDirty = serialize(form) !== baselineRef.current;

  // Восстановление черновика один раз при монтировании
  useEffect(() => {
    if (!enabled) return;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved && typeof saved === 'object' && serialize(saved) !== baselineRef.current) {
        setForm(f => ({ ...f, ...saved }));
        setRestored(true);
        track('modal_draft_restored', { scope });
      }
    } catch { /* битый черновик — игнорируем */ }
    // eslint-disable-next-line
  }, []);

  // Автосохранение (debounce). Пишем только осмысленный черновик.
  useEffect(() => {
    if (!enabled) return;
    clearTimeout(saveTimerRef.current);
    if (!isDirty) return;
    saveTimerRef.current = setTimeout(() => {
      try { localStorage.setItem(storageKey, serialize(form)); } catch { /* quota */ }
    }, 400);
    return () => clearTimeout(saveTimerRef.current);
    // eslint-disable-next-line
  }, [form, enabled, isDirty]);

  const clearDraft = useCallback((reason = 'manual') => {
    if (!enabled) return;
    try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    if (reason !== 'silent') track('modal_draft_cleared', { scope, reason });
  }, [enabled, storageKey, scope]);

  const resetForm = useCallback(() => {
    setForm(baselineObjRef.current);
    setRestored(false);
  }, [setForm]);

  const dismissRestored = useCallback(() => setRestored(false), []);

  return { restored, isDirty, clearDraft, resetForm, dismissRestored };
}

/**
 * useUnsavedGuard — обёртка над onClose с подтверждением при «грязной» форме,
 * плюс единый слушатель Escape (заменяет ручной в модале).
 *
 * @param {function} onClose      — оригинальный колбэк закрытия
 * @param {boolean}  isDirty      — есть несохранённые изменения
 * @param {string}   confirmText  — текст confirm (из i18n)
 * @param {string}   [scope]      — для аналитики
 * @returns {function} guardedClose
 */
export function useUnsavedGuard(onClose, isDirty, confirmText, scope) {
  const ref = useRef({ isDirty, onClose, confirmText, scope });
  ref.current = { isDirty, onClose, confirmText, scope };

  const guardedClose = useCallback(() => {
    const { isDirty: dirty, onClose: close, confirmText: text, scope: sc } = ref.current;
    if (dirty) {
      track('modal_close_guard_shown', { scope: sc });
      // eslint-disable-next-line no-alert
      if (!window.confirm(text)) return;
    }
    close();
  }, []);

  useEffect(() => {
    // Открываем подтверждение на keyUP, а не keydown. Если открыть нативный
    // window.confirm на keydown, трейлинг keyup того же нажатия Escape гасит только
    // что открытый диалог как «Cancel» — confirm мигает и исчезает, окно не
    // закрывается. На keyup событий того же нажатия больше нет — диалог стабилен.
    let armed = false;
    const onDown = (e) => { if (e.key === 'Escape') { armed = true; e.preventDefault(); } };
    const onUp = (e) => {
      if (e.key !== 'Escape' || !armed) return;
      armed = false;
      guardedClose();
    };
    document.addEventListener('keydown', onDown);
    document.addEventListener('keyup', onUp);
    return () => {
      document.removeEventListener('keydown', onDown);
      document.removeEventListener('keyup', onUp);
    };
  }, [guardedClose]);

  return guardedClose;
}
